import { useState, useEffect, useCallback } from "react";
import "../styles/global.css";
import Sidebar from "../components/Sidebar/Sidebar";
import Navbar from "../components/Navbar/Navbar";
import useBreakpoint from "../hooks/useBreakpoint";

const COLLAPSE_KEY = "sidebar_collapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * Sidebar behaviour
 *  desktop (>992)   : full sidebar, hamburger collapses it to an icon rail (remembered)
 *  tablet (769-992) : icon rail, hamburger expands it as an overlay
 *  mobile (<=768)   : hidden off-canvas drawer, hamburger opens it
 */
const Layout = ({ children }) => {
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === "desktop";
  const isTablet = breakpoint === "tablet";
  const isMobile = breakpoint === "mobile";

  const [collapsed, setCollapsed] = useState(readCollapsed); // desktop preference
  const [drawerOpen, setDrawerOpen] = useState(false); // tablet overlay / mobile drawer

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* storage unavailable */
    }
  }, [collapsed]);

  // Leaving tablet/mobile for desktop: drop the overlay state.
  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  // Escape closes the drawer.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKeyDown = (e) => e.key === "Escape" && setDrawerOpen(false);
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const toggleSidebar = useCallback(() => {
    if (isDesktop) setCollapsed((c) => !c);
    else setDrawerOpen((o) => !o);
  }, [isDesktop]);

  const rail = isDesktop ? collapsed : isTablet ? !drawerOpen : false;
  const offset = isMobile ? "none" : isTablet || collapsed ? "rail" : "full";
  const sidebarExpanded = isDesktop ? !collapsed : drawerOpen;

  return (
    <div className="layout">
      <Sidebar rail={rail} open={drawerOpen} onNavigate={closeDrawer} />

      {drawerOpen && !isDesktop && (
        <div className="overlay" onClick={closeDrawer} aria-hidden="true" />
      )}

      <div className="main" data-offset={offset}>
        <Navbar onMenuClick={toggleSidebar} sidebarExpanded={sidebarExpanded} />
        <div className="content">{children}</div>
      </div>
    </div>
  );
};

export default Layout;
